// Toggle requiresAuth to 'true' inside constant.js file if you want to apply authorization
import { requiresAuth, role } from "./constant.js";

// Main common page
const Login = () =>
  import(/* webpackChunkName: "Login" */ "../pages/Login/Login.vue");
const LogoutCallback = () =>
  import(
    /* webpackChunkName: "LogoutCallback" */ "../pages/LogoutCallback/LogoutCallback.vue"
  );

const SilentRenewCallback = () =>
  import(
    /* webpackChunkName: "SilentRenewCallback" */ "../pages/SilentRenewCallback/SilentRenewCallback.vue"
  );

// --> Page component with header
const Workspace = () =>
  import(
    /* webpackChunkName: "Workspace" */ "../pages/Workspace/Workspace.vue"
  );
const ErrorPage = () =>
  import(
    /* webpackChunkName: "ErrorPage" */ "../pages/ErrorPage/ErrorPage.vue"
  );
const OidcCallback = () =>
  import(
    /* webpackChunkName: "OidcCallback" */ "../pages/OidcCallback/OidcCallback.vue"
  );

// Custom common page
const UserProfile = () =>
  import(
    /* webpackChunkName: "UserProfile" */ "../pages/UserProfile/UserProfile.vue"
  );
const Survey = () =>
  import(/* webpackChunkName: "Survey" */ "../pages/Survey/Survey.vue");

// Form application Module
const MicroAppsContainer = () =>
  import(
    /* webpackChunkName: "MicroAppsContainer" */ "../module/MicroAppsContainer.vue"
  );

// Form maintenance page
const MaintenancePage = () =>
  import(
    /* webpackChunkName: "MaintenancePage" */ "./../pages/MaintenancePage/MaintenancePage.vue"
  );

const routes = [
  // -------------------------
  // START of Main Common Pages
  // -------------------------
  {
    path: "/",
    name: "Workspace",
    component: Workspace,
    meta: {
      requiresAuth,
      role
    }
  },
  {
    path: "/survey",
    name: "Survey",
    component: Survey,
    meta: {
      requiresAuth,
      role
    }
  },
  {
    path: "/login",
    name: "Login",
    component: Login
  },
  {
    path: "/user-profile",
    name: "UserProfile",
    component: UserProfile,
    meta: {
      requiresAuth,
      role
    }
  },
  {
    path: "/logoutcallback",
    name: "LogoutCallback",
    component: LogoutCallback
  },
  {
    path: "/oidccallback:_*",
    name: "OidcCallback",
    component: OidcCallback
  },
  {
    path: "/silentrenewcallback",
    name: "SilentRenewCallback",
    component: SilentRenewCallback
  },
  {
    path: "/bad-request",
    name: "BadRequest",
    component: ErrorPage,
    props: {
      errorStatus: 400
    }
  },
  {
    path: "/access-denied",
    name: "AccessDenied",
    component: ErrorPage,
    props: {
      errorStatus: 403
    }
  },
  {
    path: "/not-found",
    name: "NotFound",
    component: ErrorPage,
    props: {
      errorStatus: 404
    }
  },
  {
    path: "/request-timeout",
    name: "RequestTimeout",
    component: ErrorPage,
    props: {
      errorStatus: 408
    }
  },
  {
    path: "/maintenance",
    name: "maintenance",
    component: MaintenancePage,
    props: true
  },
  {
    path: "*",
    name: "NotFound",
    component: ErrorPage,
    props: {
      errorStatus: 404
    }
  },
  // -----------------------------------
  // START of non-form Application Modules routes
  // -----------------------------------
  // {
  //   path: "/form/audit-trail*",
  //   component: AuditTrail,
  //   meta: {
  //     requiresAuth,
  //     role
  //   }
  // }
  // ---------------------------------
  // END of Application Modules routes
  // ---------------------------------
  // -----------------------------------
  // START of form Application Modules routes
  // -----------------------------------
  {
    path: "/profile-management*",
    component: MicroAppsContainer,
    meta: {
      requiresAuth,
      role
    },
    props: {
      commonVariable: {
        container: "profile-management",
        name: "xtremax.aceas.ui.profilemanagement",
        pathname: "profile-management",
        entry: process.env.VUE_APP_PROFILE_MANAGEMENT_ENTRY
      }
    }
  },
  {
    path: "/public-register*",
    component: MicroAppsContainer,
    props: {
      commonVariable: {
        container: "public-register",
        name: "xtremax.aceas.ui.internet.publicregister",
        pathname: "public-register",
        entry: process.env.VUE_APP_PUBLIC_REGISTER_ENTRY
      }
    }
  },
  {
    path: "/suspended-or-revoked-salespersons-&-estate-agents*",
    component: MicroAppsContainer,
    props: {
      commonVariable: {
        container: "public-register",
        name: "xtremax.aceas.ui.internet.publicregister",
        pathname: "suspended-or-revoked-salespersons-&-estate-agents",
        entry: process.env.VUE_APP_PUBLIC_REGISTER_ENTRY
      }
    }
  },
  {
    path: "/rea-management*",
    component: MicroAppsContainer,
    meta: {
      requiresAuth,
      role
    },
    props: {
      commonVariable: {
        container: "rea-management",
        name: "xtremax.aceas.ui.internet.reamanagement",
        pathname: "rea-management",
        entry: process.env.VUE_APP_REA_MANAGEMENT_ENTRY
      }
    }
  },
  {
    path: "/circulars-list*",
    component: MicroAppsContainer,
    meta: {
      requiresAuth,
      role
    },
    props: {
      commonVariable: {
        container: "circulars",
        name: "xtremax.aceas.ui.internet.circularmanagement",
        pathname: "circulars-list",
        entry: process.env.VUE_APP_CIRCULARS_MANAGEMENT_ENTRY
      }
    }
  },
  {
    path: "/my-payments*",
    component: MicroAppsContainer,
    meta: {
      requiresAuth,
      role
    },
    props: {
      commonVariable: {
        container: "revenue-collection",
        name: "xtremax.aceas.ui.revenue-collection",
        pathname: "my-payments",
        entry: process.env.VUE_APP_REVENUE_COLLECTION_ENTRY
      }
    }
  },
  {
    path: "/feedback-management*",
    component: MicroAppsContainer,
    meta: {
      requiresAuth: false,
      role
    },
    props: {
      commonVariable: {
        container: "feedback-management",
        name: "xtremax.aceas.ui.internet.feedbackmanagement",
        pathname: "feedback-management",
        entry: process.env.VUE_APP_FEEDBACK_MANAGEMENT_ENTRY
      }
    },
    children: [
      {
        path: "complaint-form",
        name: "complaint-form",
        component: MicroAppsContainer,
        meta: {
          requiresAuth: true,
          role
        }
      },
      {
        path: "complaint-form/:id",
        name: "complaint-form_id",
        component: MicroAppsContainer,
        meta: {
          requiresAuth: true,
          role
        }
      }
    ]
  },
  {
    path: "/RES-exam-management*",
    component: MicroAppsContainer,
    meta: {
      requiresAuth,
      role
    },
    props: {
      commonVariable: {
        container: "res-exam-management",
        name: "xtremax.aceas.ui.internet.resexammanagement",
        pathname: "RES-exam-management",
        entry: process.env.VUE_APP_RES_EXAM_MANAGEMENT_ENTRY
      }
    }
  },
  {
    path: "/REA-exam-management*",
    component: MicroAppsContainer,
    meta: {
      requiresAuth,
      role
    },
    props: {
      commonVariable: {
        container: "rea-exam-management",
        name: "xtremax.aceas.ui.internet.reaexammanagement",
        pathname: "REA-exam-management",
        entry: process.env.VUE_APP_REA_EXAM_MANAGEMENT_ENTRY
      }
    }
  },
  {
    path: "/RES-Management*",
    component: MicroAppsContainer,
    meta: {
      requiresAuth,
      role
    },
    props: {
      commonVariable: {
        container: "res-management",
        name: "xtremax.aceas.ui.internet.resmanagement",
        pathname: "RES-Management",
        entry: process.env.VUE_APP_RES_MANAGEMENT_ENTRY
      }
    }
  },
  {
    path: "/ea-management*",
    component: MicroAppsContainer,
    meta: {
      requiresAuth,
      role
    },
    props: {
      commonVariable: {
        container: "ea-management",
        name: "xtremax.aceas.ui.internet.reamanagement",
        pathname: "ea-management",
        entry: process.env.VUE_APP_EA_MANAGEMENT_ENTRY
      }
    }
  },
  {
    path: "/pre-application*",
    component: MicroAppsContainer,
    meta: {
      requiresAuth,
      role
    },
    props: {
      commonVariable: {
        container: "pre-application",
        name: "xtremax.aceas.ui.preapplication",
        pathname: "pre-application",
        entry: process.env.VUE_APP_PREAPP_MANAGEMENT_ENTRY
      }
    }
  },
  {
    path: "/PII-management*",
    component: MicroAppsContainer,
    meta: {
      requiresAuth,
      role
    },
    props: {
      commonVariable: {
        container: "pii-management",
        name: "xtremax.aceas.ui.internet.piimanagement",
        pathname: "PII-management",
        entry: process.env.VUE_APP_PII_MANAGEMENT_ENTRY
      }
    }
  },
  {
    path: "/qualification-management*",
    component: MicroAppsContainer,
    meta: {
      requiresAuth,
      role
    },
    props: {
      commonVariable: {
        container: "qualification-management",
        name: "xtremax.aceas.ui.internet.qualificationmanagement",
        pathname: "qualification-management",
        entry: process.env.VUE_APP_QUALIFICATION_MANAGEMENT_ENTRY
      }
    }
  },
  {
    path: "/appeal-management*",
    component: MicroAppsContainer,
    meta: {
      requiresAuth,
      role
    },
    props: {
      commonVariable: {
        container: "appeal-management",
        name: "xtremax.aceas.ui.internet.appealmanagement",
        pathname: "appeal-management",
        entry: process.env.VUE_APP_APPEAL_MANAGEMENT_ENTRY
      }
    }
  }
  // ---------------------------------
  // END of Application Modules routes
  // ---------------------------------
];

export default routes;
